import React, { useState } from 'react';
import axios from 'axios';
import { format, isWeekend, previousFriday, nextMonday, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function App() {
  const [tickers, setTickers] = useState('');
  const [dateOfDeath, setDateOfDeath] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [enableSplitUnadjustment, setEnableSplitUnadjustment] = useState(false);
  const [enableAccruedDividendDetection, setEnableAccruedDividendDetection] = useState(false);
  const [processingStatus, setProcessingStatus] = useState({});
  const [showValuationInfo, setShowValuationInfo] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const [showValuationMethodologyModal, setShowValuationMethodologyModal] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const localApiBaseUrl = 'http://localhost:3001/api';
  const cloudFunctionBaseUrl = 'https://us-central1-historical-stock-value.cloudfunctions.net';

  // Helper: send analytics/log events to Cloud Functions (Cloud Logging)
  const logClientEvent = async (eventType, payload = {}) => {
    try {
      await axios.post(
        'https://us-central1-historical-stock-value.cloudfunctions.net/logClientEvent',
        {
          eventType,
          payload,
        },
        { timeout: 5000 }
      );
    } catch (e) {
      // Best-effort logging; do not surface to users
    }
  };

  // Helper function to determine if ticker is a mutual fund
  // Rule: Any 5-letter ticker ending in 'X' is a mutual fund
  const isMutualFund = (tickerSymbol) => {
    return tickerSymbol.length === 5 && tickerSymbol.endsWith('X');
  };

  // Helper function to parse ticker input
  const parseTickers = (input) => {
    return input
      .split(/[\s,\n]+/) // Split by spaces, commas, or newlines
      .map(ticker => ticker.trim().toUpperCase())
      .filter(ticker => ticker.length > 0) // Remove empty strings
      .filter((ticker, index, arr) => arr.indexOf(ticker) === index); // Remove duplicates
  };

  // Helper function to validate ticker symbols
  const validateTickerSymbols = (tickerList) => {
    const errors = [];
    
    tickerList.forEach(ticker => {
      // Check for basic format issues
      if (ticker.length < 1 || ticker.length > 10) {
        errors.push(`${ticker}: Invalid ticker length (must be 1-10 characters)`);
      }
      
      // Check for invalid characters (only letters, numbers, and some special chars)
      if (!/^[A-Z0-9.-]+$/.test(ticker)) {
        errors.push(`${ticker}: Contains invalid characters (only letters, numbers, dots, and hyphens allowed)`);
      }
      
      // Check for common typos or invalid patterns
      if (ticker.includes('..') || ticker.startsWith('.') || ticker.endsWith('.')) {
        errors.push(`${ticker}: Invalid format (cannot start/end with dots or have consecutive dots)`);
      }
    });
    
    return errors;
  };

  // Helper function to check if a date is a holiday
  const checkHoliday = async (date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const url = isLocalDevelopment
        ? `${localApiBaseUrl}/check-holiday/${dateStr}`
        : `${cloudFunctionBaseUrl}/check_holiday_firebase/${dateStr}`;

      const response = await axios.get(url, isLocalDevelopment ? {} : {
        headers: {
          'Authorization': 'Bearer your-secret-api-key-here'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking holiday status:', error);
      // Fallback to weekend check only
      return { is_holiday: isWeekend(date) };
    }
  };

  const isTickerLookupError = (error) => {
    const message = error?.message || '';
    return message.includes('Ticker symbol') || message.includes('not found') || message.includes('invalid');
  };

  // Helper function to get the previous trading day (handles weekends and holidays)
  const getPreviousTradingDay = async (date, symbol) => {
    let currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    let attempts = 0;
    
    // Keep going back until we find a day with trading data
    while (attempts < 30) {
      attempts += 1;

      // Check if it's a holiday or weekend
      const holidayInfo = await checkHoliday(currentDate);
      if (holidayInfo.is_holiday) {
        currentDate.setDate(currentDate.getDate() - 1);
        continue;
      }
      
      // Try to fetch data for this date
      try {
        await fetchStockData(symbol, currentDate);
        // If we get here, we found a trading day with data
        return currentDate;
      } catch (error) {
        if (isTickerLookupError(error)) {
          throw error;
        }

        // If no data available, go back one more day
        currentDate.setDate(currentDate.getDate() - 1);
      }
    }

    throw new Error(`Unable to find previous trading data for "${symbol}" within 30 calendar days`);
  };

  // Helper function to get the next trading day (handles weekends and holidays)
  const getNextTradingDay = async (date, symbol) => {
    let currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + 1);
    let attempts = 0;
    
    // Keep going forward until we find a day with trading data
    while (attempts < 30) {
      attempts += 1;

      // Skip weekends
      if (isWeekend(currentDate)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      
      // Try to fetch data for this date
      try {
        await fetchStockData(symbol, currentDate);
        // If we get here, we found a trading day with data
        return currentDate;
      } catch (error) {
        if (isTickerLookupError(error)) {
          throw error;
        }

        // If no data available, go forward one more day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    throw new Error(`Unable to find next trading data for "${symbol}" within 30 calendar days`);
  };

  // Helper function to fetch stock data from Yahoo Finance via our proxy server
  const fetchStockData = async (symbol, date, options = {}) => {
    try {
      // Convert date to timestamp (Unix timestamp in seconds)
      const timestamp = Math.floor(date.getTime() / 1000);
      const params = new URLSearchParams({
        period1: String(timestamp),
        period2: String(timestamp),
        interval: '1d',
      });

      if (options.dateOfDeath) {
        params.set('dodPeriod', String(Math.floor(options.dateOfDeath.getTime() / 1000)));
      }

      if (options.includeSplits) {
        params.set('includeSplits', 'true');
      }

      if (options.includeDividends) {
        params.set('includeDividends', 'true');
      }
      
      const url = isLocalDevelopment
        ? `${localApiBaseUrl}/stock-data/${symbol}?${params.toString()}`
        : `${cloudFunctionBaseUrl}/stock_data_firebase/${symbol}?${params.toString()}`;
      
      const response = await axios.get(url, {
        ...(isLocalDevelopment ? {} : {
          headers: {
            'Authorization': 'Bearer your-secret-api-key-here'
          }
        }),
        timeout: 10000
      });

      const chart = response.data?.chart;
      if (!chart || chart.error) {
        throw new Error(`No data found for ${symbol} on ${format(date, 'MMM dd, yyyy')}`);
      }
      
      const result = chart.result?.[0];
      const quote = result?.indicators?.quote?.[0];
      const timestampData = result?.timestamp?.[0];
      if (!result || !quote || timestampData == null) {
        throw new Error(`Ticker symbol "${symbol}" not found or invalid - please check the symbol and try again`);
      }
      
      // Check if we have data for the requested date
      const requestedTimestamp = Math.floor(date.getTime() / 1000);
      if (timestampData !== requestedTimestamp) {
        throw new Error(`No trading data available for ${symbol} on ${format(date, 'MMM dd, yyyy')}`);
      }

      if (quote.high?.[0] == null || quote.low?.[0] == null || quote.close?.[0] == null) {
        throw new Error(`No historical data available for "${symbol}" on ${format(date, 'MMM dd, yyyy')}`);
      }
      
      return {
        high: quote.high[0],
        low: quote.low[0],
        close: quote.close[0],
        open: quote.open[0],
        corporateActions: result.corporateActions || {
          stockSplit: { detected: false, cumulativeRatio: 1, events: [] },
          accruedDividends: { detected: false, events: [] },
        }
      };
    } catch (error) {
      if (error.response && error.response.status === 404) {
        throw new Error(`Ticker symbol "${symbol}" not found - please check the symbol and try again`);
      }
      if (error.response && error.response.status === 400) {
        throw new Error(`Invalid ticker symbol "${symbol}" - please verify the symbol format`);
      }
      if (error.response && error.response.status === 500) {
        throw new Error(`Ticker symbol "${symbol}" not found or invalid - please check the symbol and try again`);
      }
      if (error.message.includes('not found')) {
        throw new Error(`Ticker symbol "${symbol}" not found - please check the symbol and try again`);
      }
      if (error.message.includes('No data found')) {
        throw new Error(`No historical data available for "${symbol}" on ${format(date, 'MMM dd, yyyy')}`);
      }
      if (error.message.includes('Request failed with status code 500')) {
        throw new Error(`Ticker symbol "${symbol}" not found or invalid - please check the symbol and try again`);
      }
      throw error;
    }
  };

  const getActionFetchOptions = (deathDate) => ({
    dateOfDeath: deathDate,
    includeSplits: enableSplitUnadjustment,
    includeDividends: enableAccruedDividendDetection,
  });

  const getReportedPrices = (data) => {
    return {
      high: data.high,
      low: data.low,
    };
  };

  const formatCorporateActionDate = (dateString) => {
    if (!dateString) return null;
    return format(new Date(`${dateString}T00:00:00`), 'MM/dd/yy');
  };

  const getCorporateActionSummary = (data) => {
    const splitInfo = data.corporateActions?.stockSplit;
    const dividendInfo = data.corporateActions?.accruedDividends;
    const dividendEvents = dividendInfo?.events || [];

    return {
      stockSplitDetected: Boolean(splitInfo?.detected),
      stockSplitRatio: splitInfo?.cumulativeRatio || 1,
      stockSplitDate: (splitInfo?.events || []).map(event => formatCorporateActionDate(event.date)).filter(Boolean).join(', ') || null,
      stockSplitEvents: splitInfo?.events || [],
      accruedDividendDetected: dividendEvents.length > 0,
      accruedDividendDate: formatCorporateActionDate(dividendEvents[dividendEvents.length - 1]?.date),
      accruedDividendAmount: dividendEvents[dividendEvents.length - 1]?.amount || null,
      accruedDividendEvents: dividendEvents,
    };
  };

  const mergeCorporateActionSummaries = (...summaries) => {
    const splitEvents = summaries.flatMap(summary => summary.stockSplitEvents || []);
    const dividendEvents = summaries.flatMap(summary => summary.accruedDividendEvents || []);
    const latestDividend = dividendEvents[dividendEvents.length - 1];
    const splitDates = splitEvents.map(event => formatCorporateActionDate(event.date)).filter(Boolean);

    return {
      stockSplitDetected: splitEvents.length > 0,
      stockSplitRatio: summaries.find(summary => summary.stockSplitDetected)?.stockSplitRatio || 1,
      stockSplitDate: splitDates.join(', ') || null,
      stockSplitEvents: splitEvents,
      accruedDividendDetected: dividendEvents.length > 0,
      accruedDividendDate: formatCorporateActionDate(latestDividend?.date),
      accruedDividendAmount: latestDividend?.amount || null,
      accruedDividendEvents: dividendEvents,
    };
  };

  const formatSplitRatio = (ratio) => {
    if (ratio == null) return null;
    const numericRatio = Number(ratio);
    return Number.isInteger(numericRatio) ? `${numericRatio}:1` : `${ratio}:1`;
  };

  const assignCorporateActionFields = (tableData, summary) => {
    tableData.stockSplitDetected = enableSplitUnadjustment
      ? (summary.stockSplitDetected ? 'Yes' : 'No')
      : '';
    tableData.stockSplitRatio = summary.stockSplitDetected ? formatSplitRatio(summary.stockSplitRatio) : null;
    tableData.stockSplitDate = summary.stockSplitDetected ? summary.stockSplitDate : null;
    tableData.accruedDividendDetected = enableAccruedDividendDetection
      ? (summary.accruedDividendDetected ? 'Yes' : 'No')
      : '';
    tableData.accruedDividendDate = summary.accruedDividendDate;
    tableData.accruedDividendAmount = summary.accruedDividendAmount;
  };

  // Main calculation function for a single ticker
  const calculateValueForTicker = async (tickerSymbol, deathDate) => {
    const isMutualFundAsset = isMutualFund(tickerSymbol);
    const actionFetchOptions = getActionFetchOptions(deathDate);
    
    // Check if the historical date is a holiday
    const holidayInfo = await checkHoliday(deathDate);
    const isHoliday = holidayInfo.is_holiday;
    const isWeekendDay = isWeekend(deathDate);
    const isMarketHoliday = isHoliday && !isWeekendDay;

    let calculatedValue;
    let explanation;
    let ruleApplied;
    let tableData = {
      ticker: tickerSymbol,
      assetType: isMutualFundAsset ? 'Mutual Fund' : 'Stock',
      dateOfDeath: format(deathDate, 'MM/dd/yy'),
      isWeekend: isWeekendDay,
      isHoliday: isMarketHoliday,
      highPrice: null,
      lowPrice: null,
      fridayHigh: null,
      fridayLow: null,
      mondayHigh: null,
      mondayLow: null,
      previousTradingDay: null,
      previousHigh: null,
      previousLow: null,
      dodPrice: null,
      stockSplitDetected: '',
      stockSplitRatio: null,
      stockSplitDate: null,
      accruedDividendDetected: '',
      accruedDividendDate: null,
      accruedDividendAmount: null,
      ruleApplied: '',
      methodology: ''
    };

    if (isMutualFundAsset) {
      // Mutual Fund Valuation Rules
      if (isWeekendDay || isMarketHoliday) {
        // Weekend/Holiday rule for mutual funds: Use previous trading day's NAV (Close price)
        const previousTradingDay = await getPreviousTradingDay(deathDate, tickerSymbol);
        const data = await fetchStockData(tickerSymbol, previousTradingDay, actionFetchOptions);
        const actionSummary = getCorporateActionSummary(data);
        calculatedValue = data.close;
        ruleApplied = isWeekendDay ? 'Mutual Fund Weekend Rule' : 'Mutual Fund Holiday Rule';
        const dayType = isWeekendDay ? 'weekend' : 'holiday';
        
        tableData.previousTradingDay = format(previousTradingDay, 'MM/dd/yy');
        tableData.previousHigh = data.high;
        tableData.previousLow = data.low;
        tableData.dodPrice = calculatedValue;
        tableData.ruleApplied = ruleApplied;
        assignCorporateActionFields(tableData, actionSummary);
        tableData.methodology = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a ${dayType}. Using NAV from previous trading day (${format(previousTradingDay, 'MM/dd/yy')}): $${data.close.toFixed(2)}`;
        
        explanation = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a ${dayType}. Using NAV from previous trading day (${format(previousTradingDay, 'MM/dd/yy')}): $${data.close.toFixed(2)}`;
      } else {
        // Weekday rule for mutual funds: Use NAV (Close price) for that day
        const data = await fetchStockData(tickerSymbol, deathDate, actionFetchOptions);
        const actionSummary = getCorporateActionSummary(data);
        calculatedValue = data.close;
        ruleApplied = 'Mutual Fund Weekday Rule';
        
        tableData.highPrice = data.high;
        tableData.lowPrice = data.low;
        tableData.dodPrice = calculatedValue;
        tableData.ruleApplied = ruleApplied;
        assignCorporateActionFields(tableData, actionSummary);
        tableData.methodology = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a weekday. Using NAV: $${data.close.toFixed(2)}`;
        
        explanation = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a weekday. Using NAV: $${data.close.toFixed(2)}`;
      }
    } else {
      // Stock Valuation Rules
      if (isWeekendDay) {
        // Weekend rule for stocks: Average of Friday and Monday prices
        const fridayDate = await getPreviousTradingDay(deathDate, tickerSymbol);
        const mondayDate = await getNextTradingDay(deathDate, tickerSymbol);
        
        const fridayData = await fetchStockData(tickerSymbol, fridayDate, actionFetchOptions);
        const mondayData = await fetchStockData(tickerSymbol, mondayDate, actionFetchOptions);
        const fridayPrices = getReportedPrices(fridayData);
        const mondayPrices = getReportedPrices(mondayData);
        const actionSummary = mergeCorporateActionSummaries(
          getCorporateActionSummary(fridayData),
          getCorporateActionSummary(mondayData)
        );
        
        const fridayAverage = (fridayPrices.high + fridayPrices.low) / 2;
        const mondayAverage = (mondayPrices.high + mondayPrices.low) / 2;
        calculatedValue = (fridayAverage + mondayAverage) / 2;
        
        tableData.fridayHigh = fridayPrices.high;
        tableData.fridayLow = fridayPrices.low;
        tableData.mondayHigh = mondayPrices.high;
        tableData.mondayLow = mondayPrices.low;
        tableData.dodPrice = calculatedValue;
        assignCorporateActionFields(tableData, actionSummary);
        const splitNote = actionSummary.stockSplitDetected ? ' A post-date-of-death stock split was detected. The Date of Death Price stays as the Yahoo reported historical price, which may already be adjusted for the split. Review whether the reported price should be converted using the split ratio when doing the estate valuation.' : '';
        tableData.methodology = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a weekend. Averaging Friday (${format(fridayDate, 'MM/dd/yy')}) and Monday (${format(mondayDate, 'MM/dd/yy')}) prices: Friday avg $${fridayAverage.toFixed(2)}, Monday avg $${mondayAverage.toFixed(2)}, Final value $${calculatedValue.toFixed(2)}.${splitNote}`;
        
        ruleApplied = 'Stock Weekend Rule';
        tableData.ruleApplied = ruleApplied;
        explanation = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a weekend. Averaging Friday (${format(fridayDate, 'MM/dd/yy')}) and Monday (${format(mondayDate, 'MM/dd/yy')}) prices: Friday avg $${fridayAverage.toFixed(2)}, Monday avg $${mondayAverage.toFixed(2)}, Final value $${calculatedValue.toFixed(2)}.${splitNote}`;
      } else if (isMarketHoliday) {
        // Holiday rule for stocks: Average of previous and next trading days' highs and lows
        const previousTradingDay = await getPreviousTradingDay(deathDate, tickerSymbol);
        const nextTradingDay = await getNextTradingDay(deathDate, tickerSymbol);
        
        const previousData = await fetchStockData(tickerSymbol, previousTradingDay, actionFetchOptions);
        const nextData = await fetchStockData(tickerSymbol, nextTradingDay, actionFetchOptions);
        const previousPrices = getReportedPrices(previousData);
        const nextPrices = getReportedPrices(nextData);
        const actionSummary = mergeCorporateActionSummaries(
          getCorporateActionSummary(previousData),
          getCorporateActionSummary(nextData)
        );
        
        const previousAverage = (previousPrices.high + previousPrices.low) / 2;
        const nextAverage = (nextPrices.high + nextPrices.low) / 2;
        calculatedValue = (previousAverage + nextAverage) / 2;
        
        tableData.previousTradingDay = format(previousTradingDay, 'MM/dd/yy');
        tableData.nextTradingDay = format(nextTradingDay, 'MM/dd/yy');
        tableData.previousHigh = previousPrices.high;
        tableData.previousLow = previousPrices.low;
        tableData.nextHigh = nextPrices.high;
        tableData.nextLow = nextPrices.low;
        tableData.dodPrice = calculatedValue;
        assignCorporateActionFields(tableData, actionSummary);
        const splitNote = actionSummary.stockSplitDetected ? ' A post-date-of-death stock split was detected. The Date of Death Price stays as the Yahoo reported historical price, which may already be adjusted for the split. Review whether the reported price should be converted using the split ratio when doing the estate valuation.' : '';
        tableData.methodology = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a holiday. Averaging previous trading day (${format(previousTradingDay, 'MM/dd/yy')}) and next trading day (${format(nextTradingDay, 'MM/dd/yy')}) prices: Previous avg $${previousAverage.toFixed(2)}, Next avg $${nextAverage.toFixed(2)}, Final value $${calculatedValue.toFixed(2)}.${splitNote}`;
        
        ruleApplied = 'Stock Holiday Rule';
        tableData.ruleApplied = ruleApplied;
        explanation = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a holiday. Averaging previous trading day (${format(previousTradingDay, 'MM/dd/yy')}) and next trading day (${format(nextTradingDay, 'MM/dd/yy')}) prices: Previous avg $${previousAverage.toFixed(2)}, Next avg $${nextAverage.toFixed(2)}, Final value $${calculatedValue.toFixed(2)}.${splitNote}`;
      } else {
        // Weekday rule for stocks: Average of High and Low for that day
        const data = await fetchStockData(tickerSymbol, deathDate, actionFetchOptions);
        const prices = getReportedPrices(data);
        const actionSummary = getCorporateActionSummary(data);
        calculatedValue = (prices.high + prices.low) / 2;
        
        tableData.highPrice = prices.high;
        tableData.lowPrice = prices.low;
        tableData.dodPrice = calculatedValue;
        assignCorporateActionFields(tableData, actionSummary);
        const splitNote = actionSummary.stockSplitDetected ? ' A post-date-of-death stock split was detected. The Date of Death Price stays as the Yahoo reported historical price, which may already be adjusted for the split. Review whether the reported price should be converted using the split ratio when doing the estate valuation.' : '';
        tableData.methodology = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a weekday. Using average of High ($${prices.high.toFixed(2)}) and Low ($${prices.low.toFixed(2)}): $${calculatedValue.toFixed(2)}.${splitNote}`;
        
        ruleApplied = 'Stock Weekday Rule';
        tableData.ruleApplied = ruleApplied;
        explanation = `Historical date (${format(deathDate, 'MM/dd/yy')}) was a weekday. Using average of High ($${prices.high.toFixed(2)}) and Low ($${prices.low.toFixed(2)}): $${calculatedValue.toFixed(2)}.${splitNote}`;
      }
    }

    return {
      ticker: tickerSymbol,
      dateOfDeath: format(deathDate, 'MM/dd/yy'),
      assetType: isMutualFundAsset ? 'Mutual Fund' : 'Stock',
      value: calculatedValue,
      ruleApplied,
      explanation,
      tableData
    };
  };

  // Main calculation function for multiple tickers
  const calculateValues = async () => {
    const tickerList = parseTickers(tickers);
    
    if (tickerList.length === 0) {
      setError('Please enter at least one ticker symbol');
      return;
    }

    if (!dateOfDeath) {
      setError('Please enter the date');
      return;
    }

    // Validate ticker symbols before processing
    const validationErrors = validateTickerSymbols(tickerList);
    if (validationErrors.length > 0) {
      setError(`Invalid ticker symbols: ${validationErrors.join('; ')}`);
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    // Log ticker submission
    logClientEvent('submit_tickers', {
      dateOfDeath,
      tickers: tickerList,
      tickerCount: tickerList.length,
    });
    
    // Initialize processing status for all tickers
    const initialStatus = {};
    tickerList.forEach(ticker => {
      initialStatus[ticker] = 'pending';
    });
    setProcessingStatus(initialStatus);

    try {
      // Ensure proper date handling by creating date in local timezone
      const [year, month, day] = dateOfDeath.split('-').map(Number);
      const deathDate = new Date(year, month - 1, day); // month is 0-indexed

      const resultsArray = [];
      const errors = [];

      // Process each ticker
      for (const tickerSymbol of tickerList) {
        try {
          setProcessingStatus(prev => ({ ...prev, [tickerSymbol]: 'processing' }));
          
          const result = await calculateValueForTicker(tickerSymbol, deathDate);
          resultsArray.push(result);
          
          // Update results immediately as each ticker completes
          setResults([...resultsArray]);
          
          setProcessingStatus(prev => ({ ...prev, [tickerSymbol]: 'completed' }));
        } catch (error) {
          errors.push({ ticker: tickerSymbol, error: error.message });
          setProcessingStatus(prev => ({ ...prev, [tickerSymbol]: 'error' }));
        }
      }
      
      if (errors.length > 0) {
        const errorMessages = errors.map(e => `${e.ticker}: ${e.error}`).join('; ');
        setError(`Some tickers failed to process: ${errorMessages}`);
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '🔄';
      case 'completed':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  const clearResults = () => {
    setResults([]);
    setProcessingStatus({});
    setError('');
  };

  const getTotalValue = () => {
    return results.reduce((sum, result) => sum + result.value, 0);
  };

  const getVisibleColumns = () => {
    if (results.length === 0) return [];
    
    const hasWeekdayData = results.some(r => r.tableData.highPrice !== null || r.tableData.lowPrice !== null);
    const hasWeekendData = results.some(r => r.tableData.fridayHigh !== null || r.tableData.mondayHigh !== null);
    const hasHolidayData = results.some(r => r.tableData.previousTradingDay !== null);
    
    const columns = ['ticker', 'assetType', 'dateOfDeath'];

    const hasStockSplit = results.some(r => r.tableData.stockSplitDetected === 'Yes');
    const hasExDividend = results.some(r => r.tableData.accruedDividendDetected === 'Yes');

    if (enableSplitUnadjustment) {
      columns.push('stockSplitDetected');
    }

    if (hasStockSplit) {
      columns.push('stockSplitRatio', 'stockSplitDate');
    }

    if (enableAccruedDividendDetection) {
      columns.push('accruedDividendDetected');
    }

    if (hasExDividend) {
      columns.push('accruedDividendAmount', 'accruedDividendDate');
    }
    
    if (hasWeekdayData) {
      columns.push('highPrice', 'lowPrice');
    }
    
    if (hasWeekendData) {
      columns.push('fridayHigh', 'fridayLow', 'mondayHigh', 'mondayLow');
    }
    
    if (hasHolidayData) {
      columns.push('previousTradingDay', 'previousHigh', 'previousLow', 'nextTradingDay', 'nextHigh', 'nextLow');
    }
    
    columns.push('dodPrice');
    
    return columns;
  };

  const exportToCSV = () => {
    if (results.length === 0) return;

    const csvColumnDefinitions = [
      { key: 'ticker', header: 'Ticker', getValue: result => result.tableData.ticker },
      { key: 'assetType', header: 'Asset Type', getValue: result => result.tableData.assetType },
      { key: 'dateOfDeath', header: 'Valuation Date', getValue: result => result.tableData.dateOfDeath },
      { key: 'stockSplitDetected', header: 'Stock Split', getValue: result => result.tableData.stockSplitDetected || '' },
      { key: 'stockSplitRatio', header: 'Split Ratio', getValue: result => result.tableData.stockSplitRatio != null ? result.tableData.stockSplitRatio : '' },
      { key: 'stockSplitDate', header: 'Split Date', getValue: result => result.tableData.stockSplitDate || '' },
      { key: 'accruedDividendDetected', header: 'Ex-Dividend 45 Day Prior', getValue: result => result.tableData.accruedDividendDetected || '' },
      { key: 'accruedDividendAmount', header: 'Dividend Amount', getValue: result => result.tableData.accruedDividendAmount != null ? result.tableData.accruedDividendAmount.toFixed(2) : '' },
      { key: 'accruedDividendDate', header: 'Ex-Dividend Date', getValue: result => result.tableData.accruedDividendDate || '' },
      { key: 'highPrice', header: 'High Price', getValue: result => result.tableData.highPrice != null ? result.tableData.highPrice.toFixed(2) : '' },
      { key: 'lowPrice', header: 'Low Price', getValue: result => result.tableData.lowPrice != null ? result.tableData.lowPrice.toFixed(2) : '' },
      { key: 'fridayHigh', header: 'Friday High', getValue: result => result.tableData.fridayHigh != null ? result.tableData.fridayHigh.toFixed(2) : '' },
      { key: 'fridayLow', header: 'Friday Low', getValue: result => result.tableData.fridayLow != null ? result.tableData.fridayLow.toFixed(2) : '' },
      { key: 'mondayHigh', header: 'Monday High', getValue: result => result.tableData.mondayHigh != null ? result.tableData.mondayHigh.toFixed(2) : '' },
      { key: 'mondayLow', header: 'Monday Low', getValue: result => result.tableData.mondayLow != null ? result.tableData.mondayLow.toFixed(2) : '' },
      { key: 'previousTradingDay', header: 'Previous Trading Day', getValue: result => result.tableData.previousTradingDay || '' },
      { key: 'previousHigh', header: 'Previous High', getValue: result => result.tableData.previousHigh != null ? result.tableData.previousHigh.toFixed(2) : '' },
      { key: 'previousLow', header: 'Previous Low', getValue: result => result.tableData.previousLow != null ? result.tableData.previousLow.toFixed(2) : '' },
      { key: 'nextTradingDay', header: 'Next Trading Day', getValue: result => result.tableData.nextTradingDay || '' },
      { key: 'nextHigh', header: 'Next High', getValue: result => result.tableData.nextHigh != null ? result.tableData.nextHigh.toFixed(2) : '' },
      { key: 'nextLow', header: 'Next Low', getValue: result => result.tableData.nextLow != null ? result.tableData.nextLow.toFixed(2) : '' },
      { key: 'dodPrice', header: 'Date of Death Price', getValue: result => result.tableData.dodPrice != null ? result.tableData.dodPrice.toFixed(2) : '' },
      { key: 'ruleApplied', header: 'Rule Applied', getValue: result => result.tableData.ruleApplied || '' },
      { key: 'methodology', header: 'Methodology', getValue: result => result.tableData.methodology || '' },
    ];

    const csvColumns = csvColumnDefinitions.filter(column =>
      results.some(result => column.getValue(result) !== '')
    );

    const escapeCsvField = (field) => `"${String(field).replace(/"/g, '""')}"`;

    const csvContent = [
      csvColumns.map(column => column.header),
      ...results.map(result => csvColumns.map(column => column.getValue(result)))
    ].map(row => row.map(escapeCsvField).join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-valuations-${dateOfDeath}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    // Log CSV download
    logClientEvent('download_csv', {
      dateOfDeath,
      resultCount: results.length,
      tickers: results.map(r => r.tableData?.ticker).filter(Boolean),
      columns: csvColumns.map(column => column.key),
    });
  };

  const downloadPDFReport = () => {
    if (results.length === 0) return;

    const hasStockSplitPdfData = results.some(result => result.tableData.stockSplitDetected === 'Yes');
    const hasExDividendPdfData = results.some(result => result.tableData.accruedDividendDetected === 'Yes');
    const useLandscapeReport = hasStockSplitPdfData || hasExDividendPdfData;
    
    const doc = new jsPDF(useLandscapeReport ? 'landscape' : 'portrait', 'pt', 'letter');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = useLandscapeReport ? 24 : 32;
    const pdfFont = 'courier';
    const pdfFontSize = useLandscapeReport ? 8 : 10;
    const titleY = 56;
    const reportDetailsStartY = 100;
    const detailLineHeight = 14;
    const tableStartY = reportDetailsStartY + (4 * detailLineHeight) + 66;
    const reportDetailValueX = margin + 150;
    const valuationDate = format(new Date(`${dateOfDeath}T00:00:00`), 'MM/dd/yyyy');
    const reportCreationDate = format(new Date(), 'MM/dd/yyyy');

    doc.setTextColor(0, 0, 0);
    doc.setFont(pdfFont, 'normal');
    doc.setFontSize(pdfFontSize);
    doc.text('Date of Death Stock Valuation', pageWidth / 2, titleY, { align: 'center' });

    doc.setFont(pdfFont, 'normal');
    doc.setFontSize(pdfFontSize);
    const reportDetails = [
      ['Date of Death:', valuationDate],
      ['Valuation Date:', valuationDate],
      ['Report Creation Date:', reportCreationDate],
      ['Number of Securities:', String(results.length)],
    ];

    reportDetails.forEach(([label, value], index) => {
      const y = reportDetailsStartY + index * detailLineHeight;
      doc.setFont(pdfFont, 'normal');
      doc.text(label, margin, y);
      doc.setFont(pdfFont, 'normal');
      doc.text(value, reportDetailValueX, y);
    });

    const hasWeekdayPdfData = results.some(result =>
      result.tableData.highPrice != null || result.tableData.lowPrice != null
    );
    const hasWeekendPdfData = results.some(result =>
      result.tableData.fridayHigh != null || result.tableData.mondayHigh != null
    );
    const hasHolidayPdfData = results.some(result =>
      result.tableData.previousTradingDay != null || result.tableData.nextTradingDay != null
    );
    const pdfColumns = [
      { header: '', getValue: (_result, index) => `${index + 1})`, width: 28 },
      { header: 'Security', getValue: result => result.tableData.ticker, width: 54 },
      { header: 'Type', getValue: result => result.tableData.assetType, width: 54 },
      { header: 'Valuation\nDate', getValue: result => result.tableData.dateOfDeath, width: 66 },
    ];

    if (enableSplitUnadjustment) {
      pdfColumns.push({ header: 'Stock\nSplit', getValue: result => result.tableData.stockSplitDetected || '', width: 50 });
    }

    if (hasStockSplitPdfData) {
      pdfColumns.push(
        { header: 'Split\nRatio', getValue: result => result.tableData.stockSplitRatio || '-', width: 48 },
        { header: 'Split\nDate', getValue: result => result.tableData.stockSplitDate || '-', width: 66 }
      );
    }

    if (enableAccruedDividendDetection) {
      pdfColumns.push({ header: 'Ex-Dividend\n45 Day Prior', getValue: result => result.tableData.accruedDividendDetected || '', width: 82 });
    }

    if (hasExDividendPdfData) {
      pdfColumns.push(
        { header: 'Dividend\nAmount', getValue: result => result.tableData.accruedDividendAmount != null ? result.tableData.accruedDividendAmount.toFixed(2) : '-', width: 66 },
        { header: 'Ex-Dividend\nDate', getValue: result => result.tableData.accruedDividendDate || '-', width: 76 }
      );
    }

    if (hasWeekdayPdfData) {
      pdfColumns.push(
        { header: 'High/Ask', getValue: result => result.tableData.highPrice != null ? result.tableData.highPrice.toFixed(2) : '-', width: 58 },
        { header: 'Low/Bid', getValue: result => result.tableData.lowPrice != null ? result.tableData.lowPrice.toFixed(2) : '-', width: 58 }
      );
    }

    if (hasWeekendPdfData) {
      pdfColumns.push(
        { header: 'Friday High', getValue: result => result.tableData.fridayHigh != null ? result.tableData.fridayHigh.toFixed(2) : '-', width: 50 },
        { header: 'Friday Low', getValue: result => result.tableData.fridayLow != null ? result.tableData.fridayLow.toFixed(2) : '-', width: 50 },
        { header: 'Monday High', getValue: result => result.tableData.mondayHigh != null ? result.tableData.mondayHigh.toFixed(2) : '-', width: 50 },
        { header: 'Monday Low', getValue: result => result.tableData.mondayLow != null ? result.tableData.mondayLow.toFixed(2) : '-', width: 50 }
      );
    }

    if (hasHolidayPdfData && !hasWeekendPdfData) {
      pdfColumns.push(
        { header: 'Previous High', getValue: result => result.tableData.previousHigh != null ? result.tableData.previousHigh.toFixed(2) : '-', width: 58 },
        { header: 'Previous Low', getValue: result => result.tableData.previousLow != null ? result.tableData.previousLow.toFixed(2) : '-', width: 58 },
        { header: 'Next High', getValue: result => result.tableData.nextHigh != null ? result.tableData.nextHigh.toFixed(2) : '-', width: 52 },
        { header: 'Next Low', getValue: result => result.tableData.nextLow != null ? result.tableData.nextLow.toFixed(2) : '-', width: 52 }
      );
    }

    pdfColumns.push({
      header: 'Date of Death\nPrice',
      getValue: result => result.value.toFixed(2),
      width: 86,
    });

    const availableTableWidth = pageWidth - margin * 2;
    const baseTableWidth = pdfColumns.reduce((sum, column) => sum + column.width, 0);
    const widthScale = availableTableWidth / baseTableWidth;
    const scaledPdfColumns = pdfColumns.map(column => ({
      ...column,
      width: column.width * widthScale,
    }));

    autoTable(doc, {
      head: [scaledPdfColumns.map(column => column.header)],
      body: results.map((result, index) => scaledPdfColumns.map(column => column.getValue(result, index))),
      startY: tableStartY,
      margin: { left: margin, right: margin },
      theme: 'plain',
      styles: {
        font: pdfFont,
        fontStyle: 'normal',
        fontSize: pdfFontSize,
        cellPadding: useLandscapeReport
          ? { top: 7, right: 2, bottom: 7, left: 2 }
          : { top: 8, right: 4, bottom: 8, left: 4 },
        textColor: [0, 0, 0],
        lineWidth: 0,
        halign: 'left',
        overflow: 'linebreak',
      },
      headStyles: {
        fontStyle: 'normal',
        halign: 'left',
        valign: 'bottom',
        cellPadding: useLandscapeReport
          ? { top: 12, right: 2, bottom: 2, left: 2 }
          : { top: 14, right: 4, bottom: 2, left: 4 },
      },
      bodyStyles: {
        fontStyle: 'normal',
        halign: 'left',
      },
      tableWidth: availableTableWidth,
      columnStyles: scaledPdfColumns.reduce((styles, column, index) => {
        styles[index] = { cellWidth: column.width, fontStyle: 'normal', halign: 'left' };
        return styles;
      }, {}),
      didDrawCell: (data) => {
        if (data.section === 'head' && data.column.index !== 0) {
          const y = data.cell.y + data.cell.height;
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.5);
          doc.line(data.cell.x + 3, y, data.cell.x + data.cell.width - 3, y);
        }
      },
    });

    doc.addPage();
    let currentY = margin;
    const methodologyLines = [
      'Stocks: regular trading days use the mean of high and low prices. Weekend and market holiday values use nearby trading days according to the calculation details in the results.',
      'Mutual funds: 5-letter tickers ending in "X" use NAV, represented by close price data. Weekend and holiday values use the most recent previous trading day.',
      'Stock split detection: when enabled, DODSV scans for stock split events after the date of death and flags the split date and ratio. The Date of Death Price stays as the Yahoo reported historical price, which may already be adjusted for the split. Review whether the reported price should be converted using the split ratio when doing the estate valuation.',
      'Ex-dividend date detection: when enabled, DODSV scans the 45 days before the date of death for ex-dividend dates and dividend amounts. If an ex-dividend date is found, users should check whether the dividend was paid after the date of death and whether the Date of Death Price needs adjustment.',
      'Data Source: Yahoo Finance via yahooquery. Results depend on third-party data availability and quality.',
    ];

    doc.setFont(pdfFont, 'normal');
    doc.setFontSize(pdfFontSize);
    doc.text('Valuation Methodology', margin, currentY);
    currentY += 24;

    doc.setFont(pdfFont, 'normal');
    doc.setFontSize(pdfFontSize);
    methodologyLines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line, pageWidth - margin * 2);
      doc.text(wrapped, margin, currentY);
      currentY += wrapped.length * 12 + 8;
    });

    currentY += 14;
    doc.setFont(pdfFont, 'normal');
    doc.setFontSize(pdfFontSize);
    doc.text('Disclaimer', margin, currentY);
    currentY += 24;

    doc.setFont(pdfFont, 'normal');
    doc.setFontSize(pdfFontSize);
    const disclaimerText = 'DODSV calculates date of death values for educational purposes only. This tool does not provide tax, legal, or financial advice. Consult a qualified tax professional before using these results for any filing or estate decision.';
    doc.text(doc.splitTextToSize(disclaimerText, pageWidth - margin * 2), margin, currentY);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont(pdfFont, 'normal');
      doc.setFontSize(pdfFontSize);
      doc.setTextColor(90, 90, 90);
      doc.text(`Page ${i} of ${pageCount}`, margin, pageHeight - 28);
      doc.text('Generated by DODvaluation.com', pageWidth / 2, pageHeight - 28, { align: 'center' });
    }
    
    // Download
    doc.save(`date-of-death-stock-valuation-${dateOfDeath}.pdf`);

    // Log PDF download
    logClientEvent('download_pdf', {
      dateOfDeath,
      resultCount: results.length,
      tickers: results.map(r => r.tableData?.ticker).filter(Boolean),
      columns: getVisibleColumns(),
    });
  };

  return (
    <div className="container">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1 className="header-title">DODSV</h1>
          <span className="header-subtitle">Date of Death Stock Valuation</span>
        </div>
      </div>

      <div className="hero">
        <h2 className="hero-heading">Date of death stock valuation calculator</h2>
        <p className="hero-subheading">
          Calculate historical fair market values for stocks and mutual funds using the mean high-low methodology outlined in IRS Treasury Regulations.
        </p>
      </div>
      
      <div className="card valuation-form-card">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="form-group">
            <label htmlFor="tickers" className="label">
              Ticker Symbols
            </label>
            <textarea
              id="tickers"
              className="input ticker-input"
              value={tickers}
              onChange={(e) => setTickers(e.target.value)}
              placeholder="Enter tickers separated by commas, spaces, or newlines (e.g., AAPL, MSFT, VTSAX)."
              maxLength="1000"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="dateOfDeath" className="label">
              Date
            </label>
            <input
              type="date"
              id="dateOfDeath"
              className="input"
              value={dateOfDeath}
              onChange={(e) => setDateOfDeath(e.target.value)}
            />
          </div>
        </div>

        <div className="feature-options">
          <div className="feature-option">
            <span className="feature-option-content">
              <span
                className="info-tooltip"
                tabIndex="0"
                aria-label="When enabled, DODSV scans for stock split events after the date of death and flags the split date and ratio. The Date of Death Price stays as the Yahoo reported historical price, which may already be adjusted for the split. Review whether the reported price should be converted using the split ratio when doing the estate valuation."
              >
                i
                <span className="tooltip-text">
                  Scans for stock splits after the date of death and flags the split date and ratio. The Date of Death Price stays as the Yahoo reported historical price, which may already be adjusted for the split. Review whether to convert it using the split ratio.
                </span>
              </span>
              <span title="Detect for stock splits after date of death">Detect for stock splits after date of death</span>
            </span>
            <span className="yes-no-toggle" role="group" aria-label="Detect stock splits after date of death">
              <button
                type="button"
                className={`yes-no-option ${enableSplitUnadjustment ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setEnableSplitUnadjustment(true);
                }}
                aria-pressed={enableSplitUnadjustment}
              >
                Yes
              </button>
              <button
                type="button"
                className={`yes-no-option ${!enableSplitUnadjustment ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setEnableSplitUnadjustment(false);
                }}
                aria-pressed={!enableSplitUnadjustment}
              >
                No
              </button>
            </span>
          </div>
          <div className="feature-option">
            <span className="feature-option-content">
              <span
                className="info-tooltip"
                tabIndex="0"
                aria-label="This scans the 45 days before the date of death for ex-dividend dates. If one is found, the result is flagged with the ex-dividend date and dividend amount. Users should check whether the dividend was paid after the date of death and whether the Date of Death Price needs to be adjusted."
              >
                i
                <span className="tooltip-text">
                  Scans the prior 45 days for ex-dividend dates and flags the date and amount. If found, check whether the dividend was paid after death and whether the Date of Death Price needs adjustment.
                </span>
              </span>
              <span title="Detect ex-dividend date in the 45 days before date of death">Detect ex-dividend date in the 45 days before date of death</span>
            </span>
            <span className="yes-no-toggle" role="group" aria-label="Detect ex-dividend date in the 45 days before date of death">
              <button
                type="button"
                className={`yes-no-option ${enableAccruedDividendDetection ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setEnableAccruedDividendDetection(true);
                }}
                aria-pressed={enableAccruedDividendDetection}
              >
                Yes
              </button>
              <button
                type="button"
                className={`yes-no-option ${!enableAccruedDividendDetection ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setEnableAccruedDividendDetection(false);
                }}
                aria-pressed={!enableAccruedDividendDetection}
              >
                No
              </button>
            </span>
          </div>
        </div>
        
        <div className="flex justify-end mt-4">
          <button
            className="btn"
            onClick={calculateValues}
            disabled={loading || !tickers.trim() || !dateOfDeath}
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Processing Valuations...
              </>
            ) : (
              'Calculate All Valuations'
            )}
          </button>
        </div>
        <p className="methodology-note">
          See our{' '}
          <a
            href="#"
            className="methodology-note-link"
            onClick={(e) => {
              e.preventDefault();
              setShowValuationMethodologyModal(true);
            }}
          >
            Valuation Methodology
          </a>
          {' '}based on Treas. Reg. § 20.2031-2 and § 20.2031-8.
        </p>
      </div>



      {/* Error Display */}
      {error && (
        <div className="card bg-red-50 border border-red-200">
          <h3 className="text-lg font-bold text-red-600 mb-2">Error</h3>
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Results Display */}
      {results.length > 0 && (
        <div className="card bg-white">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-primary">
              Valuation Results
            </h3>
          </div>
          
          {/* Results Table */}
          <div className="overflow-x-auto w-full mb-6">
            <div className="overflow-x-auto w-full">
              <table className="w-full border-collapse results-table">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    {getVisibleColumns().map(column => {
                      const columnHeaders = {
                        ticker: ['Ticker', 'Symbol'],
                        assetType: ['Asset', 'Type'],
                        dateOfDeath: ['Valuation', 'Date'],
                        stockSplitDetected: ['Stock', 'Split'],
                        stockSplitRatio: ['Split', 'Ratio'],
                        stockSplitDate: ['Split', 'Date'],
                        accruedDividendDetected: ['Ex-Dividend', '45 Day Prior'],
                        accruedDividendAmount: ['Dividend', 'Amount'],
                        accruedDividendDate: ['Ex-Dividend', 'Date'],
                        highPrice: ['High', 'Price'],
                        lowPrice: ['Low', 'Price'],
                        fridayHigh: ['Friday', 'High'],
                        fridayLow: ['Friday', 'Low'],
                        mondayHigh: ['Monday', 'High'],
                        mondayLow: ['Monday', 'Low'],
                        previousTradingDay: ['Previous', 'Trading Day'],
                        previousHigh: ['Previous', 'High'],
                        previousLow: ['Previous', 'Low'],
                        nextTradingDay: ['Next', 'Trading Day'],
                        nextHigh: ['Next', 'High'],
                        nextLow: ['Next', 'Low'],
                        dodPrice: ['Date of Death', 'Price']
                      };
                      const headerLines = columnHeaders[column];
                      
                      return (
                        <th 
                          key={column} 
                          className={`results-table-cell column-${column} font-bold text-gray-700 ${column === 'dodPrice' ? 'bg-blue-100 text-center' : column === 'ticker' || column === 'assetType' || column === 'dateOfDeath' ? 'text-left' : 'text-center'}`}
                        >
                          <span className="table-header-title">
                            <span>{headerLines[0]}</span>
                            <span>{headerLines[1] || '\u00A0'}</span>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={result.ticker} className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      {getVisibleColumns().map(column => {
                        const value = result.tableData[column];
                        let displayValue = '-';
                        let cellClass = `results-table-cell column-${column} text-gray-700`;
                        
                        if (column === 'ticker') {
                          displayValue = value;
                          cellClass += ' font-bold text-gray-800';
                        } else if (column === 'assetType') {
                          displayValue = value;
                          cellClass += ' text-left';
                        } else if (column === 'dateOfDeath') {
                          displayValue = value;
                          cellClass += ' text-left';
                        } else if (column === 'stockSplitDetected' || column === 'accruedDividendDetected') {
                          displayValue = value || 'No';
                          cellClass += ' text-center';
                        } else if (column === 'stockSplitRatio' || column === 'stockSplitDate' || column === 'accruedDividendDate') {
                          displayValue = value || '-';
                          cellClass += ' text-center';
                        } else if (column === 'dodPrice') {
                          displayValue = `$${value.toFixed(2)}`;
                          cellClass += ' text-center font-bold text-primary bg-blue-50';
                        } else if (typeof value === 'number') {
                          displayValue = `$${value.toFixed(2)}`;
                          cellClass += ' text-center';
                        } else if (value) {
                          displayValue = value;
                          cellClass += ' text-center';
                        }
                        
                        return (
                          <td key={column} className={cellClass}>
                            {displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Loading rows */}
                  {loading && Object.entries(processingStatus).map(([ticker, status]) => {
                    if (status === 'processing') {
                      return (
                        <tr key={`loading-${ticker}`} className="border-b border-gray-200 loading-row">
                          {getVisibleColumns().map(column => {
                            let displayValue = '-';
                            let cellClass = 'p-4 text-gray-500';
                            
                            if (column === 'ticker') {
                              displayValue = ticker;
                              cellClass += ' font-bold';
                            }
                            
                            return (
                              <td key={column} className={cellClass}>
                                {displayValue}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }
                    return null;
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={exportToCSV}
              className="btn btn-secondary"
              disabled={!results || results.length === 0}
            >
              <span className="button-label-full">Export CSV</span>
              <span className="button-label-short" aria-hidden="true">CSV</span>
            </button>
            <button
              onClick={downloadPDFReport}
              className="btn btn-secondary"
              disabled={!results || results.length === 0}
            >
              <span className="button-label-full">Download Report</span>
              <span className="button-label-short" aria-hidden="true">PDF</span>
            </button>
          </div>
          
          {/* Methodology Sections */}
          <div className="mt-12 space-y-4">
            {/* Valuation Methodology Card */}
            <div 
              className="card bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setShowValuationInfo(!showValuationInfo)}
            >
              <div className="flex items-center justify-between w-full text-left py-0.5 px-2">
                <span className="text-lg font-semibold text-gray-800">Valuation Methodology</span>
              </div>
              
                              {showValuationInfo && (
                  <div className="mt-3 pl-4 border-l-2 border-gray-200 expandable-content">
                    <p className="text-xs text-gray-600 mb-4">
                      DODSV estimates date of death values for educational purposes using commonly cited valuation approaches
                      referenced in Treas. Reg. § 20.2031-2 for stocks and Treas. Reg. § 20.2031-8 for mutual funds.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="text-xs">
                        <h5 className="font-semibold text-gray-700 mb-1">Stocks</h5>
                        <p className="text-gray-600">
                          For regular trading days, DODSV calculates the mean of the high and low prices for the selected date.
                          Weekend and holiday values use nearby trading days according to the logic shown in the results.
                        </p>
                      </div>
                    <div className="text-xs">
                      <h5 className="font-semibold text-gray-700 mb-1">Mutual Funds</h5>
                      <p className="text-gray-600">
                        Mutual funds are identified as 5-letter tickers ending in "X". DODSV uses NAV, represented by close
                        price data, or the most recent previous trading day for weekends and holidays.
                      </p>
                    </div>
                    <div className="text-xs">
                      <h5 className="font-semibold text-gray-700 mb-1">Data Source</h5>
                      <p className="text-gray-600">
                        Historical pricing data is fetched from Yahoo Finance via yahooquery. Availability and quality of
                        source data may vary, so results should be independently reviewed.
                      </p>
                    </div>
                    <div className="text-xs">
                      <h5 className="font-semibold text-gray-700 mb-1">Stock Splits</h5>
                      <p className="text-gray-600">
                        When enabled, DODSV scans for stock split events after the date of death and flags the split date
                        and ratio. The Date of Death Price stays as the Yahoo reported historical price, which may already
                        be adjusted for the split. Review whether the reported price should be converted using the split
                        ratio when doing the estate valuation.
                      </p>
                    </div>
                    <div className="text-xs">
                      <h5 className="font-semibold text-gray-700 mb-1">Ex-Dividend Dates</h5>
                      <p className="text-gray-600">
                        When enabled, DODSV scans the 45 days before the date of death for ex-dividend dates and dividend
                        amounts. If one is found, check whether the dividend was paid after the date of death and whether
                        the Date of Death Price needs adjustment.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Calculation Details Card */}
            <div 
              className="card bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setShowMethodology(!showMethodology)}
            >
              <div className="flex items-center justify-between w-full text-left py-0.5 px-2">
                <span className="text-lg font-semibold text-gray-800">Calculation Details</span>
              </div>
              
                              {showMethodology && (
                  <div className="mt-3 pl-4 border-l-2 border-gray-200 expandable-content">
                    <div className="space-y-2">
                      {results.map((result, index) => (
                        <div key={result.ticker} className="text-xs">
                          <span className="font-semibold text-gray-700">{result.tableData.ticker}:</span>
                          <span className="text-gray-600 ml-2">{result.tableData.methodology}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Data Source Disclaimer */}
          <div className="card bg-blue-50 border border-blue-200">
            <h3 className="text-lg font-bold text-primary mb-2">Data Source Information</h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              <strong>Note:</strong> This application uses yahooquery to fetch historical stock data from Yahoo Finance.
              Results depend on the availability and quality of that third-party data.
            </p>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-gray-600 text-xs">
                <strong>Data Source:</strong> Yahoo Finance via yahooquery | <strong>Last Updated:</strong> {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-left">
            <p>
              DODSV calculates date of death values for educational purposes only. This tool does not provide tax, legal, or financial advice. Consult a qualified tax professional before using these results for any filing or estate decision.
            </p>
          </div>
          <div className="footer-right">
            <a href="#" onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }} className="footer-link">Privacy Policy</a>
            <a href="#" onClick={(e) => { e.preventDefault(); setShowTerms(true); }} className="footer-link">Terms of Service</a>
          </div>
        </div>
      </footer>

      {/* Valuation Methodology Modal */}
      {showValuationMethodologyModal && (
        <div className="modal-overlay" onClick={() => setShowValuationMethodologyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Valuation Methodology</h2>
              <button className="modal-close" onClick={() => setShowValuationMethodologyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="mb-4">
                DODSV estimates date of death values for educational purposes using commonly cited valuation approaches
                referenced in Treas. Reg. § 20.2031-2 for stocks and Treas. Reg. § 20.2031-8 for mutual funds.
              </p>
              <p className="mb-4">
                <strong>Stocks:</strong><br/>
                For regular trading days, DODSV calculates the mean of the high and low prices for the selected date.
                If the selected date falls on a weekend or market holiday, the tool estimates value using nearby trading
                days according to the weekend or holiday logic shown in the results.
              </p>
              <p className="mb-4">
                <strong>Mutual Funds:</strong><br/>
                Mutual funds are identified as 5-letter tickers ending in "X". For mutual funds, DODSV uses the Net Asset
                Value (NAV), represented by close price data. If the selected date is a weekend or holiday, the tool uses
                the NAV from the most recent previous trading day.
              </p>
              <p className="mb-4">
                <strong>Stock Splits:</strong><br/>
                When enabled, DODSV scans for stock split events after the date of death and flags the split date and ratio.
                The Date of Death Price stays as the Yahoo reported historical price, which may already be adjusted for the
                split. Review whether the reported price should be converted using the split ratio when doing the estate
                valuation.
              </p>
              <p className="mb-4">
                <strong>Ex-Dividend Dates:</strong><br/>
                When enabled, DODSV scans the 45 days before the date of death for ex-dividend dates and dividend amounts.
                If an ex-dividend date is found, users should check whether the dividend was paid after the date of death
                and whether the Date of Death Price needs adjustment.
              </p>
              <p className="mb-4">
                <strong>Data Source:</strong><br/>
                Historical pricing data is fetched from Yahoo Finance via yahooquery. Availability and quality of source
                data may vary, so results should be independently reviewed.
              </p>
              <p>
                <strong>Important:</strong><br/>
                DODSV is for educational purposes only and does not provide tax, legal, or financial advice. Consult a
                qualified tax professional before using any result for a filing, probate matter, or estate decision.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="modal-overlay" onClick={() => setShowPrivacy(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Privacy Policy</h2>
              <button className="modal-close" onClick={() => setShowPrivacy(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="mb-4">
                <strong>Information We Collect:</strong><br/>
                DODSV uses Google Analytics to understand how visitors use the site. Analytics may include information such as pages viewed, device and browser details, approximate location, referral source, and interaction events. DODSV also logs submitted ticker symbols and CSV/PDF download events to help monitor usage and troubleshoot the service.
              </p>
              <p className="mb-4">
                <strong>Data Usage:</strong><br/>
                The application fetches historical stock data from Yahoo Finance for calculation purposes. Analytics and event logs are used to improve the site, understand feature usage, and identify operational issues.
              </p>
              <p>
                <strong>Contact:</strong><br/>
                For privacy-related questions, please contact us through our support channels.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Terms of Service Modal */}
      {showTerms && (
        <div className="modal-overlay" onClick={() => setShowTerms(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Terms of Service</h2>
              <button className="modal-close" onClick={() => setShowTerms(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="mb-4">
                <strong>Acceptance of Terms:</strong><br/>
                By using DODSV, you agree to these terms of service. If you do not agree, please do not use the application.
              </p>
              <p className="mb-4">
                <strong>Use of Service:</strong><br/>
                DODSV is provided for educational and calculation purposes only. The tool is not intended to provide
                tax, legal, or financial advice. Consult a qualified tax professional before using results for any filing,
                probate, or estate decision.
              </p>
              <p className="mb-4">
                <strong>Analytics and Logging:</strong><br/>
                DODSV uses Google Analytics and application event logging to understand site usage, track submitted ticker symbols, and record CSV/PDF download activity. By using DODSV, you acknowledge this usage tracking.
              </p>
              <p className="mb-4">
                <strong>Data Accuracy:</strong><br/>
                We do not guarantee the completeness or accuracy of the data or calculations.
                Users should verify all results independently.
              </p>
              <p className="mb-4">
                <strong>Limitation of Liability:</strong><br/>
                DODSV and its providers are not liable for any damages arising from the use of this tool or reliance on its results.
              </p>
              <p>
                <strong>Modifications:</strong><br/>
                We reserve the right to modify these terms at any time. Continued use constitutes acceptance of any changes.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App; 